import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import api from '../lib/api'
import type { PaginatedResponse, List } from '../types'
import { formatDistanceToNow } from 'date-fns'

export default function ListsPage() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['lists'],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<List>>('/lists')
      return res.data
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map(id => api.delete(`/lists/${id}`)))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lists'] })
      setSelectedIds(new Set())
    },
  })

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedIds(newSelected)
  }

  const filteredItems = useMemo(() => {
    if (!data?.items) return []
    if (!searchQuery.trim()) return data.items
    const query = searchQuery.toLowerCase()
    return data.items.filter(l => 
      l.name.toLowerCase().includes(query) ||
      l.description?.toLowerCase().includes(query)
    )
  }, [data?.items, searchQuery])

  const toggleSelectAll = () => {
    if (!filteredItems.length) return
    if (selectedIds.size === filteredItems.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredItems.map(l => l.id)))
    }
  }

  const handleDelete = () => {
    if (selectedIds.size === 0) return
    if (confirm(`Delete ${selectedIds.size} list(s)? This cannot be undone.`)) {
      deleteMutation.mutate(Array.from(selectedIds))
    }
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Lists</h1>
          <p className="mt-1 text-sm text-gray-600">
            Static and dynamic row groupings
          </p>
        </div>
        {selectedIds.size > 0 && (
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="btn-danger"
          >
            <TrashIcon className="h-5 w-5 mr-2" />
            Delete ({selectedIds.size})
          </button>
        )}
      </div>

      {/* Search */}
      {data?.items.length ? (
        <div className="mb-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search lists..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10 w-full max-w-md"
            />
          </div>
        </div>
      ) : null}
      
      {filteredItems.length ? (
        <div className="card overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={filteredItems.length > 0 && selectedIds.size === filteredItems.length}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredItems.map((list) => (
                <tr key={list.id} className={`hover:bg-gray-50 ${selectedIds.has(list.id) ? 'bg-primary-50' : ''}`}>
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(list.id)}
                      onChange={() => toggleSelect(list.id)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <Link
                      to={`/lists/${list.id}`}
                      className="text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                      {list.name}
                    </Link>
                    {list.description && (
                      <p className="text-xs text-gray-500">{list.description}</p>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      list.list_type === 'static'
                        ? 'bg-gray-100 text-gray-700'
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {list.list_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {list.cached_count?.toLocaleString() || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatDistanceToNow(new Date(list.created_at), { addSuffix: true })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : data?.items.length ? (
        <div className="card p-12 text-center">
          <p className="text-gray-500 mb-4">No lists match "{searchQuery}"</p>
        </div>
      ) : (
        <div className="card p-12 text-center">
          <p className="text-gray-500 mb-4">No lists yet</p>
          <p className="text-sm text-gray-400">
            Lists can be created from dataset filters
          </p>
        </div>
      )}
    </div>
  )
}
